import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { BrowserModule, provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { SidebarComponent } from './components/navigation/sidebar/sidebar.component';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { OrderListPageComponent } from './pages/orders/order-list-page/order-list-page.component';
import { OrderPageComponent } from './pages/orders/order-page/order-page.component';
import { CustomerListPageComponent } from './pages/customers/customer-list-page/customer-list-page.component';
import { CustomerPageComponent } from './pages/customers/customer-page/customer-page.component';
import { AccountListPageComponent } from './pages/accounts/account-list-page/account-list-page.component';
import { AccountPageComponent } from './pages/accounts/account-page/account-page.component';
import { HeaderComponent } from './components/navigation/header/header.component';
import { SummaryCardComponent } from './components/cards/dashboard-cards/summary-card/summary-card.component';
import { OrdersCardComponent } from './components/cards/dashboard-cards/orders-card/orders-card.component';
import { IncomeCardComponent } from './components/cards/dashboard-cards/income-card/income-card.component';
import { LeadsCardComponent } from './components/cards/dashboard-cards/leads-card/leads-card.component';
import { ExpiringAccountCardComponent } from './components/cards/dashboard-cards/expiring-account-card/expiring-account-card.component';
import { PaginationComponent } from './components/pagination/pagination.component';
import { HTTP_INTERCEPTORS, HttpClientModule, provideHttpClient, withFetch } from '@angular/common/http';
import { AuthInterceptor } from './core/auth-interceptor.service';
import { environment } from '../environments/environment';
import { API_BASE_URL } from '../shared/service-proxies/event-service-proxy';
import { MainComponent } from './pages/system/main/main.component';
import { NotFoundComponent } from './pages/system/not-found/not-found.component';
import { LoginPageComponent } from './pages/auth/login-page/login-page.component';
import { ForgotPasswordPageComponent } from './pages/auth/forgot-password-page/forgot-password-page.component';
import { AuthorizeComponent } from './pages/auth/authorize/authorize.component';
import { JwtHelperService, JwtModule } from '@auth0/angular-jwt';
import { ServiceProxyModule } from '../shared/service-proxies/service-proxy.module';
import { TableComponent } from './components/table/table.component';
import { CustomerInfoCardComponent } from './components/cards/customer-cards/customer-info-card/customer-info-card.component';
import { CommentsCardComponent } from './components/cards/customer-cards/comments-card/comments-card.component';
import { CommentComponent } from './components/comment/comment.component';
import { TextAreaComponent } from './components/comment/text-area/text-area.component';
import { TableCellPipe } from './pipes/table-cell/table-cell.pipe';
import { DateFormatterPipe } from './pipes/date/date-formatter.pipe';
import { CommonModule, DatePipe } from '@angular/common';
import { ProductListPageComponent } from './pages/products/product-list-page/product-list-page.component';
import { ProductPageComponent } from './pages/products/product-page/product-page.component';
import { InputSearchComponent } from './components/inputs/input-search/input-search.component';
import { FilterBtnComponent } from './components/buttons/filter-btn/filter-btn.component';
import { FilterCustomerComponent } from './components/filter/filter-customer/filter-customer.component';
import { FilterOrderComponent } from './components/filter/filter-order/filter-order.component';
import { FilterAccountComponent } from './components/filter/filter-account/filter-account.component';
import { FilterProductComponent } from './components/filter/filter-product/filter-product.component';
import { FilterComponent } from './components/filter/filter/filter.component';
import { ModalComponent } from './components/modal/modal/modal.component';
import { ModalCreateCustomerComponent } from './components/modal/modal-create-customer/modal-create-customer.component';
import { ModalCreateProductComponent } from './components/modal/modal-create-product/modal-create-product.component';
import { FormsModule } from '@angular/forms';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { CreateOrderPageComponent } from './pages/orders/create-order-page/create-order-page.component';
import { CustomerInfoOrderCardComponent } from './components/cards/create-order-cards/customer-info-order-card/customer-info-order-card.component';
import { BillingAddressOrderCardComponent } from './components/cards/create-order-cards/billing-address-order-card/billing-address-order-card.component';
import { ProductsOrderCardComponent } from './components/cards/create-order-cards/products-order-card/products-order-card.component';
import { DeliveryAddressOrderCardComponent } from './components/cards/create-order-cards/delivery-address-order-card/delivery-address-order-card.component';
import { PaymentConfirmationOrderCardComponent } from './components/cards/create-order-cards/payment-confirmation-order-card/payment-confirmation-order-card.component';
import { SelectSearchComponent } from './components/inputs/select-search/select-search.component';
import { QuantityComponent } from './components/quantity/quantity.component';
import { MatSliderModule } from '@angular/material/slider';
import * as Sentry from '@sentry/angular';
import { LOCALE_ID } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import ruLocale from '@angular/common/locales/ru';
import { OrderInfoCardComponent } from './components/cards/order-cards/order-info-card/order-info-card.component';
import { ModalPaymentOrderComponent } from './components/modal/modal-payment-order/modal-payment-order.component';
import { ModalOrderCancelComponent } from './components/modal/modal-order-cancel/modal-order-cancel.component';
import { SkeletonComponent } from './components/skeleton/skeleton.component';
import { ToastrComponent } from './components/toastr/toastr.component';
import { ConfirmModalComponent } from './components/modal/confirm-modal/confirm-modal.component';
import { ModalProlongComponent } from './components/modal/modal-prolong/modal-prolong.component';
import { SearchBarComponent } from './components/search-bar/search-bar.component';
import { GoogleMapsModule } from '@angular/google-maps';
import { MapWorldComponent } from './components/map/map-world/map-world.component';
import { DisableAutocompleteDirective } from './directives/disable-autocomplete/disable-autocomplete.directive';
import { PhoneNumberLimitDirective } from './directives/phone-number-limit/phone-number-limit.directive';
import { InputTelComponent } from './components/inputs/input-tel/input-tel.component';
import { Router } from '@angular/router';
import { TasksComponent } from './pages/tasks/tasks.component';
import { MyTasksPageComponent } from './pages/tasks/my-tasks-page/my-tasks-page.component';
import { TaskListPageComponent } from './pages/tasks/task-list-page/task-list-page.component';
import { ModalTaskActionsComponent } from './components/modal/modal-task-actions/modal-task-actions.component';

export function getRemoteServiceBaseUrl(): string {
  return environment.apiBaseUrl;
}

registerLocaleData(ruLocale);
@NgModule({
  declarations: [
    AppComponent,
    SidebarComponent,
    DashboardComponent,
    OrderListPageComponent,
    OrderPageComponent,
    CustomerListPageComponent,
    TableComponent,
    CustomerPageComponent,
    AccountListPageComponent,
    AccountPageComponent,
    HeaderComponent,
    SummaryCardComponent,
    OrdersCardComponent,
    IncomeCardComponent,
    LeadsCardComponent,
    ExpiringAccountCardComponent,
    PaginationComponent,
    MainComponent,
    NotFoundComponent,
    LoginPageComponent,
    ForgotPasswordPageComponent,
    AuthorizeComponent,
    CustomerInfoCardComponent,
    CommentsCardComponent,
    CommentComponent,
    TextAreaComponent,
    TableCellPipe,
    DateFormatterPipe,
    ProductListPageComponent,
    ProductPageComponent,
    InputSearchComponent,
    FilterBtnComponent,
    FilterCustomerComponent,
    FilterOrderComponent,
    FilterAccountComponent,
    FilterProductComponent,
    FilterComponent,
    ModalComponent,
    ModalCreateCustomerComponent,
    ModalCreateProductComponent,
    CreateOrderPageComponent,
    CustomerInfoOrderCardComponent,
    BillingAddressOrderCardComponent,
    ProductsOrderCardComponent,
    DeliveryAddressOrderCardComponent,
    PaymentConfirmationOrderCardComponent,
    SelectSearchComponent,
    QuantityComponent,
    OrderInfoCardComponent,
    ModalPaymentOrderComponent,
    ModalOrderCancelComponent,
    SkeletonComponent,
    ToastrComponent,
    ConfirmModalComponent,
    ModalProlongComponent,
    SearchBarComponent,
    MapWorldComponent,
    DisableAutocompleteDirective,
    PhoneNumberLimitDirective,
    InputTelComponent,
    TasksComponent,
    MyTasksPageComponent,
    TaskListPageComponent,
    ModalTaskActionsComponent,
  ],
  imports: [
    BrowserModule,
    HttpClientModule,
    FormsModule,
    ServiceProxyModule,
    JwtModule.forRoot({
      config: {
        tokenGetter: () => localStorage.getItem('access_token') ?? ""
      }
    }),
    AppRoutingModule,
    NgxDaterangepickerMd.forRoot(),
    NgxMaskDirective,
    NgxMaskPipe,
    MatSliderModule,
    ScrollingModule,
    NgxSkeletonLoaderModule,
    NgxMaskDirective,
    NgxMaskPipe,
  ],
  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    // Optional: For navigation breadcrumbs
    {
  provide: Sentry.TraceService,
  useClass: Sentry.TraceService,
  deps: [Router], // 👈 это ключевой момент
},
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    provideNgxMask(),
    JwtHelperService,
    provideHttpClient(withFetch()),
    { provide: API_BASE_URL, useFactory: getRemoteServiceBaseUrl },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    DateFormatterPipe,
    DatePipe,
    provideNgxMask(),
    { provide: LOCALE_ID, useValue: 'ru' },
    provideClientHydration(withEventReplay())
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
